import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, Crown, Bookmark, StickyNote, Rocket, ScanEye } from "lucide-react";
import { LOGO_URL } from "../lib/api";

const PRO_FEATURES = [
  "A founder advisor",
  "Startup education",
  "AI guidance",
  "Workflow discovery",
  "Tool recommendations",
  "Business validation",
  "Ready-to-build blueprints",
];

const VARIANTS = {
  save: {
    icon: Bookmark,
    title: "Save this business?",
    subtitle: "Unlock LaunchBS Pro to build your startup library.",
    features: PRO_FEATURES,
  },
  note: {
    icon: StickyNote,
    title: "Create your Startup Notebook.",
    subtitle: "Save thoughts, names, ideas and plans in one place.",
    features: PRO_FEATURES,
  },
  build: {
    icon: Rocket,
    title: "Ready to build?",
    subtitle: "LaunchBS Pro turns this idea into an AI build spec.",
    features: PRO_FEATURES,
  },
  blueprint: {
    icon: Rocket,
    title: "Generate your Startup Blueprint",
    subtitle: "Unlock LaunchBS Pro to let Blueprint AI build your full, build-ready blueprint.",
    features: PRO_FEATURES,
  },
  scanner: {
    icon: ScanEye,
    title: "Unlock the AI Opportunity Scanner",
    subtitle: "Turn any photo into build-ready startup ideas with LaunchBS Pro.",
    features: PRO_FEATURES,
  },
  welcome: {
    icon: Crown,
    title: "Welcome to LaunchBS",
    subtitle: "Your Founder Account is ready. Here's everything Pro unlocks.",
    features: PRO_FEATURES,
    cta: "Start Today",
  },
  default: {
    icon: Crown,
    title: "Unlock LaunchBS Pro",
    subtitle: "Turn inspiration into action.",
    features: PRO_FEATURES,
  },
};

const PLANS = {
  annual: { label: "Annual", price: "$99", per: "/year", note: "Save 17%" },
  monthly: { label: "Monthly", price: "$9.99", per: "/month", note: null },
};

export const UpgradeModal = ({ feature, onClose, onCheckout, checkingOut }) => {
  const [plan, setPlan] = useState("annual");

  useEffect(() => {
    if (feature) setPlan("annual");
  }, [feature]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const v = VARIANTS[feature] || VARIANTS.default;
  const isLimit = feature && feature !== "default" && feature !== "welcome";
  const VIcon = v.icon;

  const handleSubscribe = () => {
    onCheckout && onCheckout(plan);
  };

  return (
    <AnimatePresence>
      {feature && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#0F0F0F]/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="upgrade-modal-overlay"
        >
          <motion.div
            className="bg-white rounded-3xl w-full max-w-md shadow-2xl relative overflow-hidden max-h-[92vh] overflow-y-auto"
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="upgrade-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors z-10"
              data-testid="upgrade-close-x"
            >
              <X size={22} />
            </button>

            {/* header */}
            <div className="bg-[#0F0F0F] px-8 pt-8 pb-10 text-center">
              <div className="flex items-center justify-center gap-2 mb-5">
                <img src={LOGO_URL} alt="LaunchBS" className="h-8 w-8 object-cover rounded-lg" />
                <span className="font-display font-extrabold tracking-tight text-white">
                  LAUNCH<span className="text-[#D4AF37]">BS</span>
                  <span className="ml-1.5 text-xs font-semibold bg-[#D4AF37] text-[#0F0F0F] px-2 py-0.5 rounded-full align-middle">
                    PRO
                  </span>
                </span>
              </div>
              <div className="w-14 h-14 mx-auto rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-[#D4AF37] mb-4">
                <VIcon size={26} />
              </div>
              <h2 className="font-display text-2xl font-bold text-white">{v.title}</h2>
              <p className="text-sm text-[#A1A1AA] mt-2 max-w-sm mx-auto">{v.subtitle}</p>
            </div>

            <div className="px-8 py-7 -mt-6">
              {isLimit && (
                <p className="text-xs text-center text-[#687280] mb-5 bg-[#F7F7F7] rounded-xl px-4 py-3">
                  You've reached your free limit. Upgrade to LaunchBS Pro to continue building your
                  startup library.
                </p>
              )}

              <ul className="space-y-2.5 mb-6">
                {v.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-[#0F0F0F]">
                    <span className="w-5 h-5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              {/* plan selector */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {Object.entries(PLANS).map(([key, p]) => (
                  <button
                    key={key}
                    onClick={() => setPlan(key)}
                    data-testid={`plan-${key}`}
                    className={`relative rounded-2xl border-2 p-4 text-left transition-all ${
                      plan === key
                        ? "border-[#D4AF37] bg-[#D4AF37]/5"
                        : "border-gray-100 hover:border-gray-200"
                    }`}
                  >
                    {p.note && (
                      <span className="absolute -top-2 right-3 text-[10px] font-semibold bg-[#D4AF37] text-[#0F0F0F] px-2 py-0.5 rounded-full">
                        {p.note}
                      </span>
                    )}
                    <p className="text-xs font-medium text-[#687280]">{p.label}</p>
                    <p className="font-display text-xl font-bold text-[#0F0F0F] mt-1">
                      {p.price}
                      <span className="text-xs font-medium text-[#687280]">{p.per}</span>
                    </p>
                  </button>
                ))}
              </div>

              <button
                onClick={handleSubscribe}
                disabled={checkingOut}
                className="w-full bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-60"
                data-testid="subscribe-btn"
              >
                {checkingOut
                  ? "Redirecting to checkout…"
                  : v.cta
                  ? `${v.cta} — ${PLANS[plan].price}${PLANS[plan].per}`
                  : `Subscribe — ${PLANS[plan].price}${PLANS[plan].per}`}
              </button>
              <button
                onClick={onClose}
                className="w-full text-center text-sm text-[#687280] hover:text-[#0F0F0F] transition-colors mt-3"
                data-testid="upgrade-maybe-later"
              >
                Maybe later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
